import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../services/api";
import collegeService from "../services/collegeService";
import defaultLogo from "../assets/images/cicoplogo.png";

/**
 * AppContext
 * ──────────
 * Stores authentication, user role, active academic year, and active financial year.
 * Automatically loads and auto-selects the active academic & financial years
 * on login and app boot so all portal users (clerk, accountant, teacher, warden, admin, etc.)
 * have immediate access to academic year data without needing to log in to Admin first.
 */

const AppContext = createContext(null);

const SESSION_KEY_AY = "cms_academic_year";
const SESSION_KEY_FY = "cms_financial_year";
const SESSION_KEY_COLLEGE_ID = "cms_selected_college_id";
const SESSION_KEY_USER = "cms_user";

const readSession = (key) => {
    try {
        const raw = sessionStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
};

const writeSession = (key, value) => {
    try {
        if (value) sessionStorage.setItem(key, JSON.stringify(value));
        else sessionStorage.removeItem(key);
    } catch { /* ignore */ }
};

// Ensure active Authorization header on page boot if session has a token
const initialToken = sessionStorage.getItem("cms_token");
if (initialToken) {
    api.defaults.headers.common["Authorization"] = `Bearer ${initialToken}`;
}

// Ensure active x-college-id header on page boot if session has one
const initialCollegeId = sessionStorage.getItem(SESSION_KEY_COLLEGE_ID);
if (initialCollegeId && initialCollegeId !== "all") {
    api.defaults.headers.common["x-college-id"] = initialCollegeId;
}

export function AppProvider({ children }) {
    const [token, setToken] = useState(() => sessionStorage.getItem("cms_token"));
    const [user, setUser] = useState(() => readSession(SESSION_KEY_USER));
    const [mustChangePw, setMustChangePw] = useState(false);

    // Year context
    const [academicYear, setAcademicYear] = useState(() => readSession(SESSION_KEY_AY));
    const [financialYear, setFinancialYear] = useState(() => readSession(SESSION_KEY_FY));
    const [academicYears, setAcademicYears] = useState([]);
    const [financialYears, setFinancialYears] = useState([]);
    const [loadingYears, setLoadingYears] = useState(false);

    // College branding & multi-tenant context
    const [selectedCollegeId, setSelectedCollegeId] = useState(() => sessionStorage.getItem(SESSION_KEY_COLLEGE_ID) || "all");
    const [collegeData, setCollegeData] = useState(null);
    const [allColleges, setAllColleges] = useState([]);

    // ── Load Years Automatically (Scoped by College) ─────────────────────────
    const loadYears = useCallback(async (authToken, targetColId) => {
        const activeToken = authToken || sessionStorage.getItem("cms_token") || token;
        if (!activeToken) return;

        const effectiveColId = targetColId !== undefined
            ? (targetColId && targetColId !== "all" ? targetColId : null)
            : (sessionStorage.getItem(SESSION_KEY_COLLEGE_ID) || null);

        setLoadingYears(true);
        try {
            const params = effectiveColId && effectiveColId !== "all" ? { college: effectiveColId } : {};
            const [ayRes, fyRes] = await Promise.allSettled([
                api.get("/academic-years", { params }),
                api.get("/financial-years", { params }),
            ]);

            if (ayRes.status === "fulfilled") {
                const list = ayRes.value.data?.data || ayRes.value.data || [];
                const safeList = Array.isArray(list) ? list : [];
                setAcademicYears(safeList);

                // Auto-select active academic year for this college
                const activeAY = safeList.find((y) => y.isActive) || safeList[0] || null;
                setAcademicYear(activeAY);
                if (activeAY) {
                    writeSession(SESSION_KEY_AY, activeAY);
                } else {
                    sessionStorage.removeItem(SESSION_KEY_AY);
                }
            }

            if (fyRes.status === "fulfilled") {
                const list = fyRes.value.data?.data || fyRes.value.data || [];
                const safeList = Array.isArray(list) ? list : [];
                setFinancialYears(safeList);

                // Auto-select active financial year for this college
                const activeFY = safeList.find((y) => y.isActive) || safeList[0] || null;
                setFinancialYear(activeFY);
                if (activeFY) {
                    writeSession(SESSION_KEY_FY, activeFY);
                } else {
                    sessionStorage.removeItem(SESSION_KEY_FY);
                }
            }
        } catch (err) {
            console.error("Failed to load academic/financial years:", err);
        } finally {
            setLoadingYears(false);
        }
    }, [token]);

    const loadAllColleges = useCallback(async () => {
        try {
            const res = await collegeService.getAll();
            const raw = res.data;
            const list = Array.isArray(raw)
                ? raw
                : Array.isArray(raw?.data)
                    ? raw.data
                    : Array.isArray(res)
                        ? res
                        : [];
            if (Array.isArray(list) && list.length > 0) {
                setAllColleges(list);
                return list;
            }
        } catch { /* try public fallback */ }

        try {
            const pubRes = await collegeService.getPublic();
            const rawPub = pubRes.data;
            const pubList = Array.isArray(rawPub)
                ? rawPub
                : Array.isArray(rawPub?.data)
                    ? rawPub.data
                    : Array.isArray(pubRes)
                        ? pubRes
                        : [];
            if (Array.isArray(pubList) && pubList.length > 0) {
                setAllColleges(pubList);
                return pubList;
            }
        } catch { /* ignore */ }
        return [];
    }, []);

    const loadCollege = useCallback(async (userData, targetColId) => {
        const u = userData || user;
        let collegeList = allColleges;
        if (!collegeList || collegeList.length === 0) {
            collegeList = await loadAllColleges();
        }
        if (!u) {
            if (collegeList && collegeList.length > 0) setCollegeData(collegeList[0]);
            return;
        }

        // For non-multi-college users, linkedCollege is authoritative
        const isMultiCollegeRole = u.role === "superadmin" || u.role === "accountant";

        if (!isMultiCollegeRole) {
            if (typeof u.linkedCollege === "object" && u.linkedCollege !== null) {
                setCollegeData(u.linkedCollege);
                return;
            }
            if (typeof u.linkedCollege === "string" && u.linkedCollege) {
                try {
                    const res = await collegeService.getById(u.linkedCollege);
                    setCollegeData(res.data?.data || res.data);
                    return;
                } catch { /* ignore */ }
            }
            if (collegeList.length > 0) setCollegeData(collegeList[0]);
            return;
        }

        // For multi-college users (superadmin and accountant):
        const colId = targetColId !== undefined
            ? targetColId
            : (sessionStorage.getItem(SESSION_KEY_COLLEGE_ID) || "all");

        if (colId && colId !== "all") {
            const match = collegeList.find(c => String(c._id) === String(colId));
            if (match) {
                setCollegeData(match);
                api.defaults.headers.common["x-college-id"] = colId;
                return;
            }
            try {
                const res = await collegeService.getById(colId);
                const fetched = res.data?.data || res.data;
                if (fetched) {
                    setCollegeData(fetched);
                    api.defaults.headers.common["x-college-id"] = colId;
                    return;
                }
            } catch { /* ignore */ }
        }

        // If "all" or not found, default to first active college for general branding, but keep selectedCollegeId = "all"
        delete api.defaults.headers.common["x-college-id"];
        if (collegeList.length > 0) setCollegeData(collegeList[0]);
    }, [user, allColleges, loadAllColleges]);

    // ── Multi-tenant College Switcher for Super Admin & Accountant ────────────
    const selectCollege = useCallback(async (collegeIdOrObj) => {
        if (!collegeIdOrObj || collegeIdOrObj === "all") {
            setSelectedCollegeId("all");
            sessionStorage.removeItem(SESSION_KEY_COLLEGE_ID);
            delete api.defaults.headers.common["x-college-id"];
            if (allColleges.length > 0) setCollegeData(allColleges[0]);
            await loadYears(token, null);
            return;
        }

        const colId = typeof collegeIdOrObj === "object" ? String(collegeIdOrObj._id) : String(collegeIdOrObj);
        setSelectedCollegeId(colId);
        sessionStorage.setItem(SESSION_KEY_COLLEGE_ID, colId);
        api.defaults.headers.common["x-college-id"] = colId;

        // Set collegeData
        let colObj = typeof collegeIdOrObj === "object" ? collegeIdOrObj : allColleges.find(c => String(c._id) === colId);
        if (!colObj) {
            try {
                const res = await collegeService.getById(colId);
                colObj = res.data?.data || res.data;
            } catch { /* ignore */ }
        }
        if (colObj) {
            setCollegeData(colObj);
        }

        // Load and sync years for this specific college
        await loadYears(token, colId);
    }, [allColleges, token, loadYears]);

    // Always fetch colleges on initial app boot
    useEffect(() => {
        loadAllColleges();
    }, [loadAllColleges]);

    useEffect(() => {
        if (user) {
            loadCollege(user);
        }
    }, [user, loadCollege]);

    // ── Year selectors ────────────────────────────────────────────────────────
    const selectAcademicYear = useCallback((year) => {
        setAcademicYear(year);
        writeSession(SESSION_KEY_AY, year);
    }, []);

    const selectFinancialYear = useCallback((year) => {
        setFinancialYear(year);
        writeSession(SESSION_KEY_FY, year);
    }, []);

    // Load years whenever token is present
    useEffect(() => {
        if (token) {
            loadYears(token);
        }
    }, [token, loadYears]);

    // ── Login success ─────────────────────────────────────────────────────────
    const loginSuccess = useCallback((newToken, userData) => {
        setToken(newToken);
        setUser(userData);
        sessionStorage.setItem("cms_token", newToken);
        writeSession(SESSION_KEY_USER, userData);
        setMustChangePw(userData.mustChangePassword || false);
        api.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
        loadYears(newToken);
        loadCollege(userData);
    }, [loadYears, loadCollege]);

    // ── Logout ────────────────────────────────────────────────────────────────
    const logout = useCallback(() => {
        setToken(null);
        setUser(null);
        setCollegeData(null);
        sessionStorage.removeItem("cms_token");
        sessionStorage.removeItem(SESSION_KEY_USER);
        sessionStorage.removeItem(SESSION_KEY_AY);
        sessionStorage.removeItem(SESSION_KEY_FY);
        sessionStorage.removeItem(SESSION_KEY_COLLEGE_ID);
        delete api.defaults.headers.common["x-college-id"];
        setAcademicYear(null);
        setFinancialYear(null);
        setAcademicYears([]);
        setFinancialYears([]);
        setMustChangePw(false);
        delete api.defaults.headers.common["Authorization"];
    }, []);

    // ── Called after successful password change ───────────────────────────────
    const clearMustChangePw = useCallback(() => {
        setMustChangePw(false);
        setUser((prev) => prev ? { ...prev, mustChangePassword: false } : prev);
    }, []);

    // ── Derived helpers ───────────────────────────────────────────────────────
    const isLoggedIn = Boolean(token);
    const role = user?.role || null;
    const isAdmin = role === "admin";
    const isTeacher = role === "teacher";
    const isStudent = role === "student";
    const isParent = role === "parent";
    const isLibrarian = role === "librarian";
    const isAccountant = role === "accountant";
    const isWarden = role === "warden";
    const isClerk = role === "clerk";
    const isStorekeeper = role === "storekeeper";
    const isStaff = ["admin", "teacher", "librarian", "accountant", "warden", "clerk", "storekeeper"].includes(role);

    const collegeName = collegeData?.name || (typeof user?.linkedCollege === "object" ? user?.linkedCollege?.name : null) || "Central India College of Pharmacy";
    const sansthaName = collegeData?.sansthaName || (typeof user?.linkedCollege === "object" ? user?.linkedCollege?.sansthaName : null);
    const logoSrc = collegeData?.logo?.url || (typeof collegeData?.logo === "string" ? collegeData.logo : "") || (typeof user?.linkedCollege?.logo === "object" ? user?.linkedCollege?.logo?.url : "") || defaultLogo;

    return (
        <AppContext.Provider value={{
            // Auth
            token, user, isLoggedIn,
            mustChangePw, clearMustChangePw,
            loginSuccess, logout,
            // Role helpers
            role, isAdmin, isTeacher, isStudent, isParent,
            isLibrarian, isAccountant, isWarden, isClerk, isStorekeeper, isStaff,
            // Year management
            academicYear, financialYear,
            academicYears, financialYears,
            loadingYears, loadYears,
            selectAcademicYear, selectFinancialYear,
            // College Branding & Multi-tenant Switching
            collegeData, collegeName, sansthaName, logoSrc, loadCollege,
            allColleges, selectedCollegeId, selectCollege,
        }}>
            {children}
        </AppContext.Provider>
    );
}

export const useApp = () => {
    const ctx = useContext(AppContext);
    if (!ctx) throw new Error("useApp must be used inside <AppProvider>");
    return ctx;
};

export default AppContext;